import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { TOURS, DESTINATIONS } from "../data/tours";
import { useAppPathname } from "../components/Seo";
import { getLocaleFromPath, HOME_TRANSLATIONS } from "../data/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback fleet — used only until the API responds (or if it is unreachable).
export const DEFAULT_FLEET = [
  {
    id: "1", slug: "mercedes-v-class", name: "Mercedes V-Class", capacity: "Up to 7 guests", price: 590,
    copy: "Leather captain's chairs, panoramic roof, Wi-Fi and refreshments — the private-tour standard.",
    image: "https://images.unsplash.com/photo-1688619102600-976b3bb41aaf?crop=entropy&cs=srgb&fm=jpg&q=85",
    description: "", features: [],
  },
  {
    id: "2", slug: "mercedes-sprinter", name: "Mercedes Sprinter", capacity: "Up to 16 guests", price: 890,
    copy: "Executive mini-coach comfort for families and small groups, with full luggage space.",
    image: "https://images.unsplash.com/photo-1688619102725-f2fd886313e6?crop=entropy&cs=srgb&fm=jpg&q=85",
    description: "", features: [],
  },
  {
    id: "3", slug: "luxury-coach", name: "Luxury Coach", capacity: "Up to 200 guests", price: 1490,
    copy: "Modern touring coaches with panoramic windows and onboard comfort — for events and big groups.",
    image: "https://images.unsplash.com/photo-1759882608768-168d4c3a91c2?crop=entropy&cs=srgb&fm=jpg&q=85",
    description: "", features: [],
  },
];

export const DEFAULT_CONTENT = {
  hero_badge: "Guided Tours of Switzerland",
  hero_line1: "Tours in Switzerland",
  hero_line2: "Made for You",
  hero_subtext:
    "Switzerland, privately yours — majestic Alps, crystal-clear lakes and storybook villages with expert local guides. 20 years of crafting unforgettable journeys, tailored entirely to you.",
  hero_image: "https://images.unsplash.com/photo-1589223050279-18d1f14059cf?crop=entropy&cs=srgb&fm=jpg&q=85",
  hero_video: "/api/media/file/voyage-swiss/media/7b231320-89f7-443d-bd19-9a1b91c4c3de.mp4",
  hero_image_alt: "Swiss Alps panorama on a Voyage Switzerland tour",
  hero_btn_primary: "Book Your Tour",
  hero_btn_secondary: "Explore Tours",
  marquee_items: [
    "Guided Swiss Tours",
    "20 Years of Experience",
    "Instant Confirmation",
    "Zero Hidden Fees",
    "24/7 Concierge",
  ],
  tours_eyebrow: "Swiss Tours",
  tours_title: "Tours in Switzerland",
  tours_subtitle:
    "Hand-crafted itineraries across Switzerland — guided, scenic and effortless. Every tour includes a local expert and flexible booking.",
  services_eyebrow: "Our Services",
  services_title: "Everything You Need in Switzerland",
  services_subtitle:
    "From private alpine adventures to seamless airport arrivals — one team for your entire Swiss journey.",
  services_items: [
    { icon: "users", title: "Private Switzerland Tours", copy: "Your own guide, your own pace — a private itinerary across Switzerland's highlights." },
    { icon: "mountain", title: "Swiss Alps & Mountain Excursions", copy: "Glacier viewpoints, panoramic railways and alpine villages, all arranged for you." },
    { icon: "landmark", title: "Lucerne, Interlaken & Zurich Tours", copy: "City gems and lake shores with local experts who know every corner." },
    { icon: "compass", title: "Customized Sightseeing Tours", copy: "Tell us your wishes — we design the perfect day around them." },
    { icon: "crown", title: "Private Chauffeur Tours", copy: "Travel in comfort with a professional driver-guide between every stop." },
    { icon: "plane", title: "Airport Transfers for Tourists", copy: "Warm meet-and-greet at Zurich or Geneva airport and a smooth ride to your hotel." },
    { icon: "package", title: "Personalized Travel Packages", copy: "Multi-day packages with hotels, tours and experiences bundled into one seamless trip." },
  ],
  dest_eyebrow: "Destinations",
  dest_title: "Top Destinations in Switzerland",
  dest_subtitle:
    "Six icons of Switzerland — pick a destination and we'll plan the perfect way to experience it.",
  why_eyebrow: "The Voyage Standard",
  why_title: "Why Choose Voyage Switzerland?",
  why_items: [
    { title: "Expert Local Guides", copy: "Every tour is led by a certified local expert who knows Switzerland inside out." },
    { title: "Trusted & Secure", copy: "Your safety and peace of mind are our top priority, with full cover on every trip." },
    { title: "Simple Booking", copy: "Book and manage your tour in just a few taps. No queues, no paperwork, no surprises." },
    { title: "Flexible & Personal", copy: "Private itineraries, flexible dates and door-to-door pickup wherever you are." },
  ],
  about_eyebrow: "About Voyage Switzerland",
  about_title: "20 Years of Swiss Hospitality",
  about_copy:
    "Voyage Switzerland started with a single belief: exploring Switzerland should feel like the start of the journey, not a chore before it. Today our concierge team pairs hand-crafted tours with an app-simple booking flow — so the only thing you plan is where to go.",
  about_image:
    "https://images.unsplash.com/photo-1775259443447-93433050f37d?crop=entropy&cs=srgb&fm=jpg&q=85",
  about_image2: "https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800&q=80",
  about_stats: [
    { value: 20, suffix: "", label: "Years of experience" },
    { value: 38, suffix: "K", label: "Happy guests" },
    { value: 10, suffix: "/10", label: "Average rating" },
    { value: 16, suffix: "", label: "Swiss destinations" },
  ],
  contact_eyebrow: "Contact",
  contact_title: "Let's plan your Swiss journey",
  contact_subtitle:
    "Tell us where you'd like to go and we'll craft the perfect tour. Questions or special requests — we answer fast.",
  contact_email: "info@voyage-day.ch",
  contact_phone: "+1 (555) 010-2847",
  contact_hours: "Open 24/7 — every day of the year",
  footer_tagline:
    "Guided tours across Switzerland. Explore unforgettable destinations with local experts — fast, easy and reliable.",
  footer_newsletter: "Tour drops, seasonal rates and travel inspiration.",
};

export const DEFAULT_SETTINGS = {
  currency: "CHF",
  primary_color: "#e11d48",
  font_pair: "swiss",
  promo_enabled: false,
  promo_text: "",
  seo_title: "Tours in Switzerland — Private & Luxury Tours | Voyage Switzerland",
  seo_description:
    "Private and luxury tours in Switzerland with local driver-guides — breathtaking Alpine scenery, tailor-made itineraries and personalized travel packages. Book your Switzerland tour today.",
  whatsapp: "",
  ga_measurement_id: "",
  gsc_verification: "",
};

export const FONT_PAIRS = {
  swiss: {
    label: "Swiss Modern",
    display: "Outfit",
    body: "Plus Jakarta Sans",
    href: "https://fonts.googleapis.com/css2?family=Outfit:wght@400..900&family=Plus+Jakarta+Sans:wght@400..700&display=swap",
  },
  elegant: {
    label: "Elegant Editorial",
    display: "Fraunces",
    body: "Manrope",
    href: "https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400..900&family=Manrope:wght@400..800&display=swap",
  },
  modern: {
    label: "Bold Modern",
    display: "Sora",
    body: "DM Sans",
    href: "https://fonts.googleapis.com/css2?family=Sora:wght@400..800&family=DM+Sans:opsz,wght@9..40,400..800&display=swap",
  },
  classic: {
    label: "Classic Serif",
    display: "Playfair Display",
    body: "Source Sans 3",
    href: "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500..900&family=Source+Sans+3:wght@400..700&display=swap",
  },
};

const CURRENCY_SYMBOLS = { CHF: "CHF ", EUR: "€", USD: "$", GBP: "£" };

const clamp = (n) => Math.max(0, Math.min(255, Math.round(n)));

const shadeHex = (hex, pct) => {
  const n = parseInt(hex.replace("#", ""), 16);
  const f = 1 + pct / 100;
  const r = clamp(((n >> 16) & 255) * f);
  const g = clamp(((n >> 8) & 255) * f);
  const b = clamp((n & 255) * f);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const softHex = (hex) => {
  const n = parseInt(hex.replace("#", ""), 16);
  const mix = (c) => clamp(c + (255 - c) * 0.94);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
};

const ARABIC_FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800;900&display=swap";

const TEXT_SIZES = { compact: "15px", default: "16px", large: "17.5px" };
const HERO_SIZES = {
  compact: "clamp(260px, 45vw, 480px)",
  default: "clamp(300px, 55vw, 580px)",
  large: "clamp(340px, 62vw, 680px)",
};
const TOUR_IMG_SIZES = { compact: "11rem", default: "14rem", large: "17rem" };
const DEST_IMG_SIZES = { compact: "16rem", default: "20rem", large: "24rem" };

const applyTheme = (settings, locale) => {
  const root = document.documentElement;
  root.style.setProperty("--brand", settings.primary_color);
  root.style.setProperty("--brand-dark", shadeHex(settings.primary_color, -22));
  root.style.setProperty("--brand-soft", softHex(settings.primary_color));
  // Admin-controlled sizes (Design tab): global text scale + media sizes.
  root.style.fontSize = TEXT_SIZES[settings.text_size] || TEXT_SIZES.default;
  root.style.setProperty("--hero-h", HERO_SIZES[settings.hero_media_size] || HERO_SIZES.default);
  root.style.setProperty("--tour-img-h", TOUR_IMG_SIZES[settings.card_image_size] || TOUR_IMG_SIZES.default);
  root.style.setProperty("--dest-img-h", DEST_IMG_SIZES[settings.card_image_size] || DEST_IMG_SIZES.default);
  // Arabic pages switch to a native Arabic typeface in both roles.
  const pair = locale === "ar" ? null : FONT_PAIRS[settings.font_pair] || FONT_PAIRS.swiss;
  root.style.setProperty("--font-display", `"${pair ? pair.display : "Tajawal"}", sans-serif`);
  root.style.setProperty("--font-body", `"${pair ? pair.body : "Tajawal"}", sans-serif`);
  let link = document.getElementById("brand-fonts");
  if (!link) {
    link = document.createElement("link");
    link.id = "brand-fonts";
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = pair ? pair.href : ARABIC_FONT_HREF;
};

const SiteDataContext = createContext(null);
export const useSiteData = () => useContext(SiteDataContext);

// Website Studio: read a section's published design overrides (text per-device, colors, bg image).
export const useSectionDesign = (key) => {
  const ctx = useContext(SiteDataContext);
  const s = ctx?.siteDesign?.[key] || {};
  const device = typeof window !== "undefined" ? (window.innerWidth < 640 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop") : "desktop";
  const text = (tkey, fallback) => s.texts?.[tkey]?.[device] || s.texts?.[tkey]?.desktop || fallback;
  const bg = s.background || {};
  const bgUrl = (device === "mobile" && bg.url_mobile) ? bg.url_mobile
    : (device === "tablet" && bg.url_tablet) ? bg.url_tablet
    : bg.url || s.images?.bg || "";
  return {
    hidden: !!s.hidden,
    text,
    bgImage: bgUrl,
    accent: s.accent_color || "",
    bgColor: s.bg_color || "",
    textColor: s.text_color || "",
    background: {
      enabled: bg.enabled !== false && !!bgUrl,
      url: bgUrl,
      position: bg.position || "center",
      size: bg.size || "cover",
      overlay_color: bg.overlay_color || "#0b132b",
      overlay_opacity: bg.overlay_opacity != null ? bg.overlay_opacity : 0,
      scrim: bg.scrim != null ? bg.scrim : 0.85,
      scrim_direction: bg.scrim_direction || "left",
      fixed: !!bg.fixed,
      height: bg.height || "",
      raw: bg,
    },
    video: {
      enabled: s.video?.enabled !== false,
      url: s.video?.url || "",
    },
  };
};

export const SiteDataProvider = ({ children }) => {
  const [contentBase, setContentBase] = useState(DEFAULT_CONTENT);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [tours, setTours] = useState(TOURS);
  const [destinations, setDestinations] = useState(DESTINATIONS);
  const [fleet, setFleet] = useState(DEFAULT_FLEET);
  const [theme, setTheme] = useState(null);
  const [siteDesign, setSiteDesign] = useState(null);

  const refreshTheme = useCallback(async () => {
    try {
      const preview = new URLSearchParams(window.location.search).get("theme_preview");
      const { data } = await axios.get(`${API}/themes/active`, { params: preview ? { preview } : {} });
      setTheme(data.theme || null);
    } catch {
      setTheme(null);
    }
  }, []);
  const pathname = useAppPathname();
  const locale = getLocaleFromPath(pathname);
  const content = { ...contentBase, ...(HOME_TRANSLATIONS[locale] || {}) };

  const refreshContent = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      setContentBase({ ...DEFAULT_CONTENT, ...data });
    } catch {
      /* keep defaults */
    }
  }, []);

  const refreshSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`);
      setSettings({ ...DEFAULT_SETTINGS, ...data });
    } catch {
      /* keep defaults */
    }
  }, []);

  const refreshTours = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tours`);
      if (Array.isArray(data)) setTours(data);
    } catch {
      /* keep defaults */
    }
  }, []);

  const refreshDestinations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/destinations`);
      if (Array.isArray(data)) setDestinations(data);
    } catch {
      /* keep defaults */
    }
  }, []);

  const refreshFleet = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/fleet`);
      if (Array.isArray(data) && data.length) setFleet(data);
    } catch {
      /* keep defaults */
    }
  }, []);

  const lastFetch = useRef(0);

  // One request instead of five; falls back to the individual endpoints.
  const loadAll = useCallback(async () => {
    lastFetch.current = Date.now();
    try {
      const { data } = await axios.get(`${API}/bootstrap`);
      if (data?.content && data?.settings) {
        setContentBase(data.content);
        setSettings(data.settings);
        setTours(data.tours || []);
        setDestinations(data.destinations || []);
        setFleet(data.fleet || []);
        if (!new URLSearchParams(window.location.search).get("theme_preview")) {
          setTheme(data.theme || null);
        }
      }
    } catch { /* fall through to individual fetches */
      refreshContent();
      refreshSettings();
      refreshTours();
      refreshDestinations();
      refreshFleet();
    }
    // Site design overrides (section visibility, colors, texts) — independent of bootstrap.
    try {
      const sd = await axios.get(`${API}/site-design`);
      if (sd.data?.sections) setSiteDesign(sd.data.sections);
    } catch { /* optional */ }
  }, [refreshContent, refreshSettings, refreshTours, refreshDestinations, refreshFleet]);

  useEffect(() => {
    loadAll();
    refreshTheme();
  }, [loadAll, refreshTheme]);

  // Live theme updates: poll every 4s so admin theme changes (activate /
  // deactivate / Save & Publish) appear for visitors who already have the
  // site open — no refresh needed. Lightweight endpoint, skipped in preview mode.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("theme_preview")) return;
    const id = setInterval(refreshTheme, 4000);
    return () => clearInterval(id);
  }, [refreshTheme]);

  // Refetch when a visitor returns to the tab (throttled), so admin edits
  // appear for everyone without a manual refresh — without doubling requests.
  useEffect(() => {
    const onFocus = () => {
      if (Date.now() - lastFetch.current < 30000) return;
      loadAll();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadAll]);

  useEffect(() => {
    applyTheme(settings, locale);
  }, [settings, locale]);

  // Active seasonal theme can override the accent color and font pairing.
  useEffect(() => {
    const root = document.documentElement;
    const design = theme?.design || {};
    const accent = design.accent_color;
    if (accent) {
      root.style.setProperty("--seasonal-accent", accent);
      root.style.setProperty("--seasonal-soft", softHex(accent));
    } else {
      root.style.removeProperty("--seasonal-accent");
      root.style.removeProperty("--seasonal-soft");
    }
    // Site-wide theme page design: text + button colors on dark/transformed themes.
    const pageSpec = { ...(design.page?.all || {}) };
    if (pageSpec.text_color) {
      root.style.setProperty("--seasonal-text", pageSpec.text_color);
      document.body.style.color = pageSpec.text_color;
      document.body.style.background = "transparent";
    } else {
      root.style.removeProperty("--seasonal-text");
      document.body.style.color = "";
      document.body.style.background = "";
    }
    if (pageSpec.button_color) {
      root.style.setProperty("--seasonal-accent", pageSpec.button_color);
      root.style.setProperty("--seasonal-soft", softHex(pageSpec.button_color));
    }
    const pair = design.font_pair && FONT_PAIRS[design.font_pair];
    let link = document.getElementById("seasonal-fonts");
    if (pair && locale !== "ar") {
      root.style.setProperty("--font-display", `"${pair.display}", sans-serif`);
      if (!link) {
        link = document.createElement("link");
        link.id = "seasonal-fonts";
        link.rel = "stylesheet";
        document.head.appendChild(link);
      }
      link.href = pair.href;
    } else {
      applyTheme(settings, locale); // restore brand fonts
      if (link) link.remove();
    }
  }, [theme, settings, locale]);

  const fmtPrice = useCallback(
    (n) => `${CURRENCY_SYMBOLS[settings.currency] || `${settings.currency} `}${n}`,
    [settings.currency]
  );

  return (
    <SiteDataContext.Provider
      value={{
        content,
        settings,
        tours,
        destinations,
        refreshContent,
        refreshSettings,
        refreshTours,
        refreshDestinations,
        refreshFleet,
        fleet,
        theme,
        refreshTheme,
        siteDesign,
        fmtPrice,
        locale,
      }}
    >
      {children}
    </SiteDataContext.Provider>
  );
};
